import React, { createContext, useContext, useState, useEffect } from 'react';
import analyticsService from '../utils/analytics';
import { debugLog } from '../utils/debug';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      validateToken(token);
    } else {
      setIsLoading(false);
    }
  }, []);

  const validateToken = async (token) => {
    debugLog('[Auth] validateToken called, token exists:', !!token);
    try {
      const response = await fetch('/api/auth/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      debugLog('[Auth] /api/auth/me response status:', response.status);

      if (response.ok) {
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const textResponse = await response.text();
          if (process.env.NODE_ENV === 'development') {
            console.error('[ERROR] /api/auth/me returned HTML:', textResponse.substring(0, 200));
          }
          localStorage.removeItem('authToken');
          return;
        }
        
        const userData = await response.json();
        debugLog('[Auth] User data from /api/auth/me:', userData);

        // Handle both response formats:
        // Format 1: { user: {...}, subscription: {...} }
        // Format 2: { id, email, subscription, ... } (user data at top level)
        const user = userData.user || (userData.id ? userData : null);

        if (!user || !user.id) {
          console.error('[Auth] /api/auth/me returned no user data:', userData);
          localStorage.removeItem('authToken');
          setUser(null);
          return;
        }

        // Get subscription from response (could be nested or at top level)
        const subscription = userData.subscription;

        // Include subscription tier information in user object
        const userWithSubscription = {
          ...user,
          subscription: subscription,
          tier: subscription ? subscription.tier : 'free',
          paymentStatus: userData.paymentStatus || user.paymentStatus || 'inactive'
        };

        // Double-check tier is set correctly
        if (!userWithSubscription.tier || userWithSubscription.tier === 'undefined') {
          console.warn('⚠️ [Auth] Tier not properly set, defaulting to free');
          userWithSubscription.tier = 'free';
        }

        setUser(userWithSubscription);
        debugLog('[Auth] User authenticated successfully:', user.email);

        // Track user in Google Analytics
        if (user.id) {
          analyticsService.setUserId(user.id, {
            subscriptionTier: userWithSubscription.tier,
            userType: user.type || 'standard',
            signupDate: user.createdAt,
            paymentStatus: userWithSubscription.paymentStatus
          });
        }
      } else {
        console.error('[Auth] validateToken failed with status:', response.status);
        const errorText = await response.text();
        console.error('[Auth] Error response:', errorText);
        localStorage.removeItem('authToken');
        setUser(null);
      }
    } catch (error) {
      console.error('[Auth] Token validation error:', error.message);
      localStorage.removeItem('authToken');
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);

        // Validate token to get full user/subscription data from /api/auth/me
        debugLog('🔍 [Auth] Login successful, fetching full subscription data...');
        await validateToken(data.token);

        // Note: setUser is called by validateToken with full subscription data
        // Track login in Google Analytics (user state will be updated by validateToken)
        analyticsService.setUserId(data.user.id, {
          subscriptionTier: data.user.subscription?.tier || 'free',
          userType: data.user.type || 'standard',
          signupDate: data.user.createdAt,
          paymentStatus: data.user.paymentStatus || 'inactive'
        });
        analyticsService.trackLogin('email', data.user.subscription?.tier || 'free');

        return data;
      } else {
        throw new Error(data.error || 'Login failed');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (email, password) => {
    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);
        setUser(data.user);

        // Track signup in Google Analytics
        analyticsService.setUserId(data.user.id, {
          subscriptionTier: 'free',
          userType: 'standard',
          signupDate: new Date().toISOString()
        });
        analyticsService.trackSignup('email');

        return data;
      } else {
        throw new Error(data.error || 'Registration failed');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const googleLogin = async (redirect, action) => {
    try {
      // Build query params
      const params = new URLSearchParams();
      if (redirect) params.append('redirect', redirect);
      if (action) params.append('action', action);

      const url = `/api/auth/google${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url);
      const data = await response.json();

      if (response.ok) {
        window.location.href = data.authUrl;
      } else {
        throw new Error(data.error || 'Google login failed');
      }
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const handleGoogleCallback = async (code, state) => {
    setError(null);
    setIsLoading(true);

    try {
      if (process.env.NODE_ENV === 'development') {
        debugLog('[DEBUG] Google callback with code:', code);
        debugLog('[DEBUG] OAuth state:', state);
      }
      const response = await fetch('/api/auth/google/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code, state })
      });
      if (process.env.NODE_ENV === 'development') {
        debugLog('[DEBUG] Google callback response status:', response.status);
        debugLog('[DEBUG] Google callback response headers:', Object.fromEntries(response.headers.entries()));
      }

      // Check content type before trying to parse as JSON
      const contentType = response.headers.get('content-type');
      if (process.env.NODE_ENV === 'development') {
        debugLog('[DEBUG] Response content type:', contentType);
      }
      
      if (!contentType || !contentType.includes('application/json')) {
        const textResponse = await response.text();
        if (process.env.NODE_ENV === 'development') {
          console.error('[ERROR] Expected JSON but got:', textResponse.substring(0, 200));
        }
        throw new Error(`Server returned HTML instead of JSON. Content-Type: ${contentType}`);
      }

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('authToken', data.token);

        // Validate token to get full user/subscription data from /api/auth/me
        debugLog('🔍 [Auth] Google login successful, fetching full subscription data...');
        await validateToken(data.token);

        // Note: setUser is called by validateToken with full subscription data
        // Track Google login in Google Analytics (user state will be updated by validateToken)
        analyticsService.setUserId(data.user.id, {
          subscriptionTier: data.user.subscription?.tier || 'free',
          userType: data.user.type || 'standard',
          signupDate: data.user.createdAt,
          paymentStatus: data.user.paymentStatus || 'inactive'
        });
        analyticsService.trackLogin('google', data.user.subscription?.tier || 'free');

        return data;
      } else {
        throw new Error(data.error || 'Google authentication failed');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[ERROR] Google callback error:', error);
      }
      setError(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Logout request failed:', error);
      }
    } finally {
      // Clear analytics user ID and track logout
      analyticsService.trackLogout();
      analyticsService.clearUserId();

      localStorage.removeItem('authToken');
      setUser(null);

      // Redirect to homepage and clear the page
      window.location.href = '/';
    }
  };

  const updateUser = (userData) => {
    setUser(prevUser => ({ ...prevUser, ...userData }));
  };

  const refreshUserData = async () => {
    const token = localStorage.getItem('authToken');
    if (token) {
      await validateToken(token);
    }
  };

  const isAuthenticated = !!user;
  // Free plan users should be considered as having an "active subscription" for basic features
  // Only block users with failed or cancelled payments
  const hasActiveSubscription = user && (user.paymentStatus !== 'failed' && user.paymentStatus !== 'cancelled');

  const value = {
    user,
    isLoading,
    error,
    isAuthenticated,
    hasActiveSubscription,
    login,
    register,
    googleLogin,
    handleGoogleCallback,
    logout,
    updateUser,
    refreshUserData,
    clearError: () => setError(null)
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}